/**
 * Billing account layout — route shell for a single billing account's
 * subviews (invoices, pricing, prepaid, trial units, catalog).
 *
 * Owns the account fetch, the Account Management title bar, a permanent
 * detail rail (account identity, active-agreement management, and notes —
 * an inline rail beside the stage on `lg`+, a collapsible summary above
 * the stage below that), and the surface-chromed stage card with its route-driven
 * tab strip — each tab is a real child route rendered through Outlet rather
 * than an MUI TabPanel, with the loaded account exposed to them via
 * useBillingAccount(). There is no General tab: its content (agreements,
 * notes) lives permanently in the rail instead, and the index route
 * redirects to Invoices, the default landing tab. Pre-Paid is a normal tab
 * (AccountPrepaidView).
 *
 * Contains:
 * - BillingAccountLayout — route layout component (default export)
 * - useBillingAccount — child-route access to the loaded account; throws
 *   outside the layout
 */
import { TabContext, TabList } from '@mui/lab';
import { Box, CircularProgress, Paper, Skeleton, Stack, Tab, useMediaQuery } from '@mui/material';
import React, { createContext, Suspense, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { loadBillingAccount } from '@sophocles/features/billing/api';
import { selectBillingAccount } from '@sophocles/features/billing/slice';
import {
  billingAccountCatalogPath,
  billingAccountInvoicesPath,
  billingAccountPath,
  billingAccountPrepaidPath,
  billingAccountPricingPath,
  billingAccountTrialPath,
} from '@sophocles/router/paths';
import AccountMobileSummary from '@sophocles/views/billing/account/AccountMobileSummary';
import BillingAccountRail from '@sophocles/views/billing/account/BillingAccountRail';
import { BillingTitleBar } from '@sophocles/views/billing/components';

// Grid-hosting subviews bound the height chain so their grids scroll
// internally — an unbounded panel gives infinite-scroll grids an infinite
// viewport, and lazy loading then fetches the entire result set.
const GRID_TAB_PANEL_SX = { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' };

// Document-style subviews scroll their own content instead of the page.
const DOC_TAB_PANEL_SX = { flex: 1, minHeight: 0, overflow: 'auto' };

// Per-tab panel bounding, keyed by the active child route segment. Mirrors
// each subview's TabPanel sx, including MUI's default p:3 for tabs that
// don't override it. Invoices and Prepaid own their internal padding (p:0).
const ACCOUNT_PANEL_SX = {
  invoices: { ...GRID_TAB_PANEL_SX, p: 0 },
  pricing: { ...DOC_TAB_PANEL_SX, p: 2 },
  prepaid: { ...GRID_TAB_PANEL_SX, p: 0 },
  trial: { ...GRID_TAB_PANEL_SX, p: 2 },
  catalog: { ...DOC_TAB_PANEL_SX, p: 3 },
};

// Tab value → child-route path builder. Invoices is the default/index
// landing tab (see routes/billing.js's index redirect).
const ACCOUNT_TAB_PATHS = {
  invoices: billingAccountInvoicesPath,
  pricing: billingAccountPricingPath,
  prepaid: billingAccountPrepaidPath,
  trial: billingAccountTrialPath,
  catalog: billingAccountCatalogPath,
};

// Fixed desktop rail width — wide enough for full agreement management
// (AgreementCard, older agreements, notes), not just a summary.
const DESKTOP_RAIL_WIDTH = 340;

// Suspense fallback for lazy subview chunks: skeleton only the stage panel so
// a tab's first visit never blanks the shell (the app-level RouteLoader
// fallback would otherwise take over the whole screen).
function AccountPanelSkeleton() {
  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, p: 2 }}>
      <Skeleton variant="rounded" height={48} sx={{ maxWidth: 480 }} />
      <Skeleton variant="rounded" sx={{ flex: 1 }} />
    </Stack>
  );
}

const BillingAccountContext = createContext(null);

/**
 * Child-route access to the billing account loaded by BillingAccountLayout.
 *
 * @returns {{ account: Object|undefined }} the layout's context value.
 */
export function useBillingAccount() {
  const value = useContext(BillingAccountContext);
  if (!value) throw new Error('useBillingAccount must be used inside BillingAccountLayout');
  return value;
}

function BillingAccountLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { accountId } = useParams();

  const account = useSelector(selectBillingAccount(accountId));

  useEffect(() => {
    if (!account) {
      dispatch(loadBillingAccount(accountId));
    }
  }, [accountId]);

  const basePath = billingAccountPath({ accountId });
  const activeTab = useMemo(() => {
    const segment = location.pathname.slice(basePath.length).replace(/^\//, '').split('/')[0];
    return segment || 'invoices';
  }, [location.pathname, basePath]);

  const contextValue = useMemo(() => ({ account }), [account]);

  // Three rail tiers: >= lg gets the permanent inline rail; the md-lg band
  // trades it for the collapsible summary bar (a fixed 340px rail starves the
  // stage at intermediate widths — grids and stat cards crush); < md is the
  // same summary treatment.
  const useCompactRail = useMediaQuery(theme => theme.breakpoints.down('lg'));

  return (
    <>
      <BillingTitleBar
        title={account?.customer?.name ?? t('billing.account.title')}
        crumbs={[{ label: t('billing.accountManagement', 'Account Management'), to: '/billing/accounts' }]}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          px: 'var(--page-px)',
          pb: 'var(--page-py)',
        }}
      >
        {useCompactRail && <AccountMobileSummary account={account} />}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: useCompactRail ? 0 : 'var(--page-gap)' }}>
          {!useCompactRail && (
            <Box
              sx={{
                width: DESKTOP_RAIL_WIDTH,
                flexShrink: 0,
                overflow: 'auto',
                p: 2,
                bgcolor: 'var(--card-bg)',
                border: 1,
                borderColor: 'var(--card-border)',
                borderRadius: 'var(--card-radius)',
                boxShadow: 'var(--card-shadow)',
              }}
            >
              <BillingAccountRail account={account} />
            </Box>
          )}
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              bgcolor: 'var(--card-bg)',
              border: 1,
              borderColor: 'var(--card-border)',
              borderRadius: 'var(--card-radius)',
              boxShadow: 'var(--card-shadow)',
            }}
          >
            <TabContext value={activeTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <TabList
                  onChange={(e, newTab) => navigate(ACCOUNT_TAB_PATHS[newTab]({ accountId }))}
                  aria-label={t('billing.accountTabs', 'Account Tabs')}
                >
                  <Tab label={t('billing.account.tabInvoices')} value="invoices" />
                  <Tab label={t('billing.account.tabPricing')} value="pricing" />
                  <Tab label={t('billing.account.tabPrepaid')} value="prepaid" />
                  <Tab label={t('billing.account.tabTrialUnits')} value="trial" />
                  <Tab label={t('billing.account.tabCatalog')} value="catalog" />
                </TabList>
              </Box>
            </TabContext>
            <Box sx={ACCOUNT_PANEL_SX[activeTab] ?? DOC_TAB_PANEL_SX}>
              {account == null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <BillingAccountContext.Provider value={contextValue}>
                  {/* Local boundary: a lazy subview chunk skeletons only this
                      panel — without it the app-level Suspense fallback blanks
                      the whole screen on each tab's first visit. */}
                  <Suspense fallback={<AccountPanelSkeleton />}>
                    <Outlet />
                  </Suspense>
                </BillingAccountContext.Provider>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

export default BillingAccountLayout;
